import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { endpoints } from '../api/endpoints';
import type { Session } from '../api/types';
import { ComparisonPlayer } from '../components/compare/ComparisonPlayer';
import { ErrorBox, formatDate, useAsync } from '../components/common';

function TrackPicker({ label, onPick }: { label: string; onPick: (src: string) => void }) {
  const { t } = useTranslation();
  const [url, setUrl] = useState('');
  return (
    <fieldset className="card">
      <legend>{label}</legend>
      <label>
        {t('compare.file')}
        <input type="file" accept="audio/*" onChange={(e) => e.target.files?.[0] && onPick(URL.createObjectURL(e.target.files[0]))} />
      </label>
      <div className="inline-form">
        <label>
          {t('compare.url')}
          <input type="url" value={url} onChange={(e) => setUrl(e.target.value)} />
        </label>
        <button type="button" className="secondary" disabled={!url} onClick={() => onPick(url)}>
          {t('compare.load')}
        </button>
      </div>
    </fieldset>
  );
}

export function ComparePage() {
  const { t, i18n } = useTranslation();
  const [params, setParams] = useSearchParams();
  const sessionId = params.get('session') ?? '';
  const step = params.get('step') ?? '';
  const sessions = useAsync(() => endpoints.sessions.list({ limit: 50 }).catch(() => [] as Session[]), []);
  const session = useAsync(() => (sessionId ? endpoints.sessions.get(sessionId) : Promise.resolve(null)), [sessionId]);
  const [reference, setReference] = useState<string | null>(null);
  const [attempt, setAttempt] = useState<string | null>(null);

  const details = session.data?.details ?? [];
  useEffect(() => {
    const d = details.find((x) => String(x.step_order) === step);
    if (d) {
      setReference(d.ai_response_audio_url);
      setAttempt(d.user_audio_url);
    }
  }, [step, details]);

  return (
    <div>
      <h1>{t('compare.title')}</h1>
      <p className="muted">{t('compare.hint')}</p>
      <ErrorBox error={session.error} />
      <div className="filters">
        <label>
          {t('compare.selectSession')}
          <select value={sessionId} onChange={(e) => setParams({ session: e.target.value })}>
            <option value="">—</option>
            {(sessions.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {formatDate(s.started_at, i18n.language)}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('compare.selectStep')}
          <select value={step} disabled={!details.length} onChange={(e) => setParams({ session: sessionId, step: e.target.value })}>
            <option value="">—</option>
            {details
              .filter((d) => d.user_audio_url || d.ai_response_audio_url)
              .map((d) => (
                <option key={d.step_order} value={d.step_order}>
                  #{d.step_order} {d.user_input_text?.slice(0, 40)}
                </option>
              ))}
          </select>
        </label>
      </div>
      <div className="grid-2">
        <TrackPicker label={t('compare.reference')} onPick={setReference} />
        <TrackPicker label={t('compare.attempt')} onPick={setAttempt} />
      </div>
      <ComparisonPlayer referenceSrc={reference} attemptSrc={attempt} />
    </div>
  );
}
